import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { AttachmentTile } from '../../components/ui/AttachmentTile';
import { Sheet } from '../../components/ui/Sheet';
import { AvatarSymbolPicker } from '../../components/ui/AvatarSymbolPicker';
import { CameraCapture } from './CameraCapture';
import { ATTACHMENT_SYMBOL_CHOICES } from '../../lib/icons';
import { uid, type Attachment } from '../../types';

const ATTACHMENT_COLORS = ['#60A5FA', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#14B8A6'];

export function AddPostScreen() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { addPost } = useStore();

  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const save = () => {
    if (!channelId) return;
    addPost({ id: uid(), title, link, description, attachments }, channelId);
    navigate(`/channels/${channelId}`);
  };

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Add Post" action={<IconButton icon={Check} onClick={save} aria-label="Save post" />} />

      <FormSection title="Compose">
        <TextField label="Post title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField label="Link" value={link} onChange={(e) => setLink(e.target.value)} />
        <TextArea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
      </FormSection>

      <FormSection title="Gallery">
        <div className="flex gap-3">
          <Button variant="bordered" onClick={() => setShowLibrary(true)}>
            Attach from library
          </Button>
          <Button variant="bordered" onClick={() => setShowCamera(true)}>
            Open camera
          </Button>
        </div>
        {attachments.length > 0 && (
          <div className="flex gap-2.5 overflow-x-auto pt-1">
            {attachments?.map((a) => (
              <AttachmentTile key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </FormSection>

      <Sheet open={showLibrary} onClose={() => setShowLibrary(false)} title="Attach from library">
        <AvatarSymbolPicker
          symbols={ATTACHMENT_SYMBOL_CHOICES}
          colors={ATTACHMENT_COLORS}
          onPick={(symbol, colorHex) => {
            setAttachments((prev) => [...prev, { id: uid(), symbol, colorHex, title: symbol }]);
            setShowLibrary(false);
          }}
        />
      </Sheet>

      <CameraCapture
        open={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={(attachment) => setAttachments((prev) => [...prev, attachment])}
      />
    </div>
  );
}
